import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { formatPrice } from '../lib/currency';
import { toast } from 'sonner';
import { Plus, Eye, MousePointerClick, TrendingUp, FileText, Loader2, Sparkles, Wallet, Megaphone, Trash2, Send, Pencil } from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-muted text-muted-foreground', pending_review: 'bg-amber-500/15 text-amber-700',
  changes_requested: 'bg-orange-500/15 text-orange-700', approved: 'bg-sky-500/15 text-sky-700',
  published: 'bg-emerald-500/15 text-emerald-700', rejected: 'bg-destructive/15 text-destructive',
  invited: 'bg-blue-500/15 text-blue-700', applied: 'bg-amber-500/15 text-amber-700',
  accepted: 'bg-emerald-500/15 text-emerald-700', active: 'bg-emerald-500/15 text-emerald-700',
  completed: 'bg-sky-500/15 text-sky-700', declined: 'bg-muted text-muted-foreground',
};
const fmtS = (s) => (s || '').replace(/_/g, ' ');

export default function CreatorDashboard() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { currency } = useApp();
  const [creator, setCreator] = useState(null);
  const [checked, setChecked] = useState(false);
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [available, setAvailable] = useState([]);

  useEffect(() => { document.title = 'Creator Studio | FlightDeals.in'; if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);

  const reload = () => {
    api.creatorItineraries().then(({ data }) => setItems(data.itineraries)).catch(() => {});
    api.creatorAnalytics().then(({ data }) => setAnalytics(data)).catch(() => {});
    api.creatorEarnings().then(({ data }) => setEarnings(data)).catch(() => {});
    api.creatorCampaigns().then(({ data }) => setCampaigns(data.campaigns)).catch(() => {});
    api.creatorAvailableCampaigns().then(({ data }) => setAvailable(data.campaigns)).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    api.creatorMe().then(({ data }) => {
      setCreator(data.is_creator ? data.creator : null); setChecked(true);
      if (data.is_creator && data.creator.approval_status === 'approved') reload();
    }).catch(() => setChecked(true));
  }, [user]);

  if (loading || !checked) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;

  if (!creator || creator.approval_status !== 'approved') {
    return <Layout><div className="max-w-xl mx-auto py-24 text-center px-4">
      <Sparkles className="h-10 w-10 mx-auto text-accent mb-3" />
      <h1 className="font-display text-2xl font-semibold">{creator ? `Application ${creator.approval_status}` : 'Become a creator'}</h1>
      <p className="text-muted-foreground mt-1">{creator ? 'Your creator account is not yet approved.' : 'Apply to start building itineraries.'}</p>
      {!creator && <Button onClick={() => navigate('/creators/join')} className="mt-4 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="dash-apply-cta">Apply now</Button>}
    </div></Layout>;
  }

  const s = analytics?.summary;
  const del = async (id) => { try { await api.creatorDeleteItinerary(id); setItems((x) => x.filter((i) => i.itinerary_id !== id)); toast.success('Deleted'); } catch (e) { toast.error(e?.response?.data?.detail || 'Cannot delete'); } };
  const submit = async (id) => { try { await api.creatorSubmitItinerary(id); toast.success('Submitted for review'); reload(); } catch (e) { toast.error(e?.response?.data?.detail || 'Cannot submit'); } };
  const respond = async (id, accept) => { try { await api.creatorRespondCampaign(id, accept); toast.success(accept ? 'Campaign accepted' : 'Declined'); reload(); } catch { toast.error('Failed'); } };
  const applyCampaign = async (id) => { try { await api.creatorApplyCampaign(id, ''); toast.success('Application sent!'); reload(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };

  const KPI = ({ icon: Ic, label, value }) => (
    <Card className="p-4 rounded-2xl"><div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Ic className="h-4 w-4 text-accent" /> {label}</div><p className="text-2xl font-semibold tabular">{value}</p></Card>
  );

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-12 w-12 rounded-2xl bg-accent text-accent-foreground text-lg font-semibold overflow-hidden">{creator.profile_photo ? <img src={api.mediaUrl(creator.profile_photo)} alt="" className="h-full w-full object-cover" /> : (creator.name||'C').charAt(0)}</span>
            <div><h1 className="font-display text-2xl font-semibold">Creator Studio</h1><p className="text-sm text-muted-foreground">@{creator.username} · {creator.verification_status}</p></div>
          </div>
          <Button onClick={() => navigate('/creator/itineraries/new')} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="create-itinerary-button"><Plus className="h-4 w-4 mr-1.5" /> New itinerary</Button>
        </div>

        <Tabs defaultValue="overview" data-testid="creator-dashboard-tabs">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="ct-overview">Overview</TabsTrigger>
            <TabsTrigger value="itineraries" data-testid="ct-itineraries">My Itineraries ({items.length})</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="ct-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="earnings" data-testid="ct-earnings">Earnings</TabsTrigger>
            <TabsTrigger value="campaigns" data-testid="ct-campaigns">Campaigns ({campaigns.length + available.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <div className="grid gap-3 grid-cols-2 md:grid-cols-4" data-testid="creator-overview-kpis">
              <KPI icon={FileText} label="Itineraries" value={s?.total_itineraries ?? 0} />
              <KPI icon={Eye} label="Total views" value={s?.views ?? 0} />
              <KPI icon={MousePointerClick} label="Affiliate clicks" value={s?.affiliate_clicks ?? 0} />
              <KPI icon={TrendingUp} label="Confirmed (₹)" value={(s?.confirmed_commission ?? 0).toLocaleString('en-IN')} />
              <KPI icon={FileText} label="Published" value={s?.published ?? 0} />
              <KPI icon={FileText} label="Drafts" value={s?.drafts ?? 0} />
              <KPI icon={FileText} label="Pending" value={s?.pending ?? 0} />
              <KPI icon={TrendingUp} label="CTR %" value={s?.ctr ?? 0} />
            </div>
          </TabsContent>

          <TabsContent value="itineraries" className="mt-4">
            {items.length === 0 ? (
              <div className="text-center py-14 rounded-2xl border border-dashed border-border"><FileText className="h-9 w-9 mx-auto mb-2 opacity-40" /><p className="text-sm text-muted-foreground">No itineraries yet. Create your first!</p></div>
            ) : (
              <div className="space-y-3" data-testid="creator-itineraries-list">
                {items.map((it) => (
                  <Card key={it.itinerary_id} className="p-4 rounded-2xl flex items-center gap-3">
                    <img src={it.cover_image || 'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=200&q=60'} alt="" className="h-14 w-20 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{it.title}</p>
                      <p className="text-xs text-muted-foreground">{it.destination} · {it.duration_days}d · {it.stats?.views || 0} views</p>
                    </div>
                    <Badge className={`rounded-full text-[10px] ${STATUS_COLORS[it.status] || ''}`}>{it.status.replace('_', ' ')}</Badge>
                    <div className="flex items-center gap-1">
                      {(it.status === 'draft' || it.status === 'changes_requested') && <Button size="icon" variant="ghost" onClick={() => submit(it.itinerary_id)} title="Submit" data-testid={`submit-${it.itinerary_id}`}><Send className="h-4 w-4" /></Button>}
                      <Button size="icon" variant="ghost" onClick={() => navigate(`/creator/itineraries/${it.itinerary_id}`)} title="Edit" data-testid={`edit-${it.itinerary_id}`}><Pencil className="h-4 w-4" /></Button>
                      {it.status !== 'published' && <Button size="icon" variant="ghost" onClick={() => del(it.itinerary_id)} title="Delete"><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="analytics" className="mt-4">
            {!analytics ? <p className="text-muted-foreground">Loading…</p> : (
              <Card className="p-5 rounded-2xl">
                <h3 className="font-semibold mb-3">Performance by itinerary</h3>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Itinerary</th><th>Status</th><th className="text-right">Views</th><th className="text-right">Clicks</th><th className="text-right">Conv.</th></tr></thead>
                  <tbody>{analytics.per_itinerary.map((r) => (
                    <tr key={r.itinerary_id} className="border-b border-border last:border-0"><td className="py-2 pr-2 truncate max-w-[220px]">{r.title}</td><td><Badge className={`text-[10px] ${STATUS_COLORS[r.status]}`}>{r.status.replace('_',' ')}</Badge></td><td className="text-right tabular">{r.views}</td><td className="text-right tabular">{r.affiliate_clicks}</td><td className="text-right tabular">{r.conversions}</td></tr>
                  ))}</tbody>
                </table>
                {analytics.per_itinerary.length === 0 && <p className="text-sm text-muted-foreground">No data yet.</p>}
              </Card>
            )}
          </TabsContent>

          <TabsContent value="earnings" className="mt-4">
            {!earnings ? <p className="text-muted-foreground">Loading…</p> : (
              <>
                <Card className="p-5 rounded-2xl mb-3"><div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Wallet className="h-4 w-4 text-accent" /> Confirmed commission (your share)</div><p className="text-3xl font-semibold tabular">{formatPrice(earnings.confirmed_total, currency)}</p><p className="text-xs text-muted-foreground mt-1">Only confirmed partner commissions are counted. Payouts are recorded in the ledger; money is not sent automatically.</p></Card>
                <Card className="p-5 rounded-2xl" data-testid="creator-earnings-ledger">
                  <h3 className="font-semibold mb-3">Payout ledger</h3>
                  {earnings.payouts.length === 0 ? <p className="text-sm text-muted-foreground">No payouts yet.</p> : (
                    <table className="w-full text-sm"><thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Conversion</th><th>Gross</th><th>Your share</th><th>Status</th></tr></thead>
                    <tbody>{earnings.payouts.map((p) => (<tr key={p.payout_id} className="border-b border-border last:border-0"><td className="py-2 font-mono text-xs">{p.conversion_id}</td><td className="tabular">{formatPrice(p.gross_commission, currency)}</td><td className="tabular font-medium">{formatPrice(p.creator_share, currency)}</td><td><Badge variant="outline" className="text-[10px]">{p.status}</Badge></td></tr>))}</tbody></table>
                  )}
                </Card>
              </>
            )}
          </TabsContent>

          <TabsContent value="campaigns" className="mt-4 space-y-6">
            {/* Available campaigns */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Megaphone className="h-4 w-4 text-accent" /> Available campaigns</h3>
              {available.length === 0 ? (
                <p className="text-sm text-muted-foreground">No open campaigns right now. Check back soon.</p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2" data-testid="available-campaigns">
                  {available.map((c) => (
                    <Card key={c.campaign_id} className="p-4 rounded-2xl flex flex-col" data-testid={`available-campaign-${c.campaign_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div><p className="font-medium text-sm">{c.name}</p><p className="text-xs text-muted-foreground">{c.brand || 'FlightDeals'} · {c.destination || 'Any'}</p></div>
                        <Badge variant="outline" className="text-[10px] rounded-full">{fmtS(c.deal_type)}</Badge>
                      </div>
                      {c.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{c.description}</p>}
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                        {c.fixed_fee > 0 && <span>Fee: {formatPrice(c.fixed_fee, currency)}</span>}
                        {c.affiliate_commission_rate != null && <span>· Affiliate {Math.round(c.affiliate_commission_rate*100)}%</span>}
                        {c.performance_bonus > 0 && <span>· Bonus {formatPrice(c.performance_bonus, currency)}</span>}
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        {c.eligible ? <span className="text-[11px] text-emerald-600">You're eligible</span> : <span className="text-[11px] text-amber-600">Eligibility not fully met</span>}
                        <Button size="sm" onClick={() => applyCampaign(c.campaign_id)} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid={`apply-campaign-${c.campaign_id}`}>Apply</Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* My campaigns grouped */}
            <div>
              <h3 className="font-semibold mb-3">My campaigns ({campaigns.length})</h3>
              {campaigns.length === 0 ? (
                <div className="text-center py-10 rounded-2xl border border-dashed border-border"><Megaphone className="h-9 w-9 mx-auto mb-2 opacity-40" /><p className="text-sm text-muted-foreground">No campaign participations yet.</p></div>
              ) : (
                <div className="space-y-3" data-testid="my-campaigns">
                  {campaigns.map((c) => {
                    const st = c.participation_status || c.contract_status;
                    const ag = c.agreement || c;
                    return (
                      <Card key={c.campaign_id} className="p-4 rounded-2xl flex items-center justify-between gap-3">
                        <div><p className="font-medium text-sm">{c.title}</p><p className="text-xs text-muted-foreground">{fmtS(ag.deal_type || c.deal_type)} · {ag.fixed_fee ? formatPrice(ag.fixed_fee, currency) : 'affiliate'} <Badge className={`text-[10px] rounded-full ml-1 ${STATUS_COLORS[st]||''}`}>{fmtS(st)}</Badge></p></div>
                        {st === 'invited' && <div className="flex gap-2"><Button size="sm" onClick={() => respond(c.campaign_id, true)} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid={`accept-${c.campaign_id}`}>Accept</Button><Button size="sm" variant="outline" onClick={() => respond(c.campaign_id, false)} className="rounded-xl">Decline</Button></div>}
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
